// ---------- 确保最早 hook 全局 console（必须为首个 import） ----------
import '@/capacitor/logger/logger-boot'; // hookConsole() 在此立即执行
import 'virtual:svg-icons-register';

// ========== 基础与 UI 模块 ==========
import { createApp } from 'vue';
import { loadComponent } from 'd3components/src/utils/componentRegistry';

import App from './App.vue';
import { i18n } from '@/i18n';
import router from '@/router';
import { adService } from '@/layout/services/AdService';
// 提前全局暴露，供组件库立即可取用
(window as any).__APP_ROUTER__ = router;
import { initPinia } from 'd3components';
import { useUserStore } from 'd3components';
import { registerForLogin } from 'd3components/src/components/actionUtils/loginUtil';
import platform from './utils/platform'

import './assets/app.css';
import './assets/D3.css';


// import Varlet from '@varlet/ui';
// import '@varlet/ui/es/style';
// import 'vue-sonner/style.css';
import 'd3components/src/components/item-d3/third/d3_toast.vue'
// ========== Capacitor / 本地功能 ==========
import { Capacitor } from '@capacitor/core';
import { createFile } from '@/capacitor/file/createfile';
import { statusBarAdapter } from '@/capacitor/statusbar/statusbaradapter';
import ALLComponent from '@/components'
import { navigationManager } from '@/capacitor/exited/navigationmanager';
import { initLogger } from '@/capacitor/logger/initlogger';
import { initHotUpdateManager } from '@/capacitor/updater/inithotupdatemanager';
import { hotUpdateManager } from '@/capacitor/updater/hotupdate';

// // ========== 租户初始化（在所有模块导入后，应用创建前） ==========
// import { initTenant } from '@/utils/tenant-init';
// initTenant();
import { bootstrapAuthAndRoutes } from '@/permission';
import { SplashScreen } from '@capacitor/splash-screen';
import { getKeyStr ,removeKey, setKey, setKeyJsonStr ,getKeyJson,updateKeyJson,getsubKey, removeKeys} from 'd3components/src/utils/localStorage';



const t0 = performance.now(); (window as any).__t0 = t0;
// const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
// 启动前置初始化最多等待 1.5 秒，避免广告/状态栏等初始化把原生启动页拖太久。
const PRE_MOUNT_INIT_TIMEOUT_MS = 1500;

// 防止多处逻辑重复调用 SplashScreen.hide。
let nativeSplashHidden = false;

// 等浏览器完成一帧绘制，确保 Vue 首屏或广告层已经有内容可接住原生启动页。
const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

// 给启动阶段的异步任务加超时兜底；超时后继续启动流程，不再阻塞原生启动页隐藏。
const withTimeout = async <T>(task: Promise<T>, timeoutMs: number, label: string): Promise<T | undefined> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      task,
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => {
          console.warn(`${label} timed out after ${timeoutMs}ms`);
          resolve(undefined);
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

// 统一隐藏原生启动页，并记录隐藏原因和耗时，便于真机日志排查启动时间。
const hideNativeSplash = async (reason: string) => {
  if (!Capacitor.isNativePlatform() || nativeSplashHidden) return;
  nativeSplashHidden = true;
  try {
    await SplashScreen.hide({ fadeOutDuration: 150 });
    console.log('Splash hidden:', reason, Math.round(performance.now() - t0));
  } catch (e) {
    console.warn('隐藏原生启动页失败:', e);
  }
};

//前面的初始化或渲染流程异常，4 秒后也尝试隐藏原生启动页。
if (Capacitor.isNativePlatform()) {
  window.setTimeout(() => {
    hideNativeSplash('timeout-fallback');
  }, 4000);
}

// ------------------------------------------------------
// 点击反馈：仅对“用户刚触摸/点击后触发”的路由导航，延迟一小段时间
//
// 背景：
// - 我们的菜单/卡片很多是用 div + :active 做按下态。
// - 一旦点击马上路由跳转，当前页面 DOM 会立刻卸载，:active 基本看不到。
//
// 解决思路：
// - 监听全局的用户手势（pointerdown/touchstart），记录时间戳。
// - 包装 router.push/router.replace：
//   - 如果导航发生在“手势时间戳之后的短窗口内”，认为是用户触发，则 setTimeout 延迟后再导航。
//   - 否则（权限守卫重定向、程序性跳转）不延迟，避免影响系统逻辑。
// ------------------------------------------------------
// NAV_DELAY_MS：真正延迟的时间（毫秒）。越大越能看到按下态，但跳转会更“慢”。
const NAV_DELAY_MS = 100;
// USER_GESTURE_WINDOW_MS：在这个时间窗口内发生的导航，才被认为是“用户点击触发”。
// 例如：用户点一下 -> 同步触发 action -> router.push，很快发生（几十毫秒）。
const USER_GESTURE_WINDOW_MS = 150;
// lastUserGestureAt：记录最近一次用户手势发生的时间戳（Date.now()）。0 表示从未记录过。
let lastUserGestureAt = 0;

// markUserGesture：更新 lastUserGestureAt，用于后续判断“这次导航是否紧随用户手势”。
const markUserGesture = () => (lastUserGestureAt = Date.now());
// 监听在 capture 阶段，尽可能早记录到手势；passive 提升滚动/触摸性能。
window.addEventListener('pointerdown', markUserGesture, { capture: true, passive: true });
window.addEventListener('touchstart', markUserGesture, { capture: true, passive: true });

// withUserNavDelay：包装 router.push/replace。
// - 仅当判断为用户触发的导航时延迟
// - 其它情况保持原行为（不延迟）
// const withUserNavDelay = <T extends (...args: any[]) => any>(fn: T): ((...args: Parameters<T>) => any) => {
//   return (...args: Parameters<T>) => {
//     const isUserTriggeredNav = Date.now() - lastUserGestureAt <= USER_GESTURE_WINDOW_MS;
//     if (!isUserTriggeredNav) return fn(...args);
//     return new Promise((resolve, reject) => {
//       setTimeout(() => {
//         Promise.resolve(fn(...args)).then(resolve).catch(reject);
//       }, NAV_DELAY_MS);
//     });
//   };
// };

// // 只包装 push/replace（常见的页面跳转入口）。
// // 注意：此处是运行时替换方法，不改变 router 实例本身的其它行为。
// (router as any).push = withUserNavDelay((router as any).push.bind(router));
// (router as any).replace = withUserNavDelay((router as any).replace.bind(router));
// ------------------------------------------------------
// Vue 应用创建与基础插件注入
// ------------------------------------------------------
const app = createApp(App);

// try { navigationManager.init(router); console.log('导航已成功初始化'); } catch (e) { console.error('导航管理器初始化失败:', e); }
if (!platform.isMiniProgramEnv()) {
  navigationManager.init(router)
  console.log('导航已成功初始化')
} else {
  console.log('微信环境，跳过 NavigationManager 初始化')
}

// 路由
app.use(router);
app.use(ALLComponent as any)
// 国际化
app.use(i18n);
// 状态管理 - 使用 d3components 提供的 Pinia 工厂，与 web 项目保持一致
const pinia = initPinia();
app.use(pinia as any);
// 登录/退出逻辑依赖：将 mobile 的 router/route/userStore 
// registerForLogin(router.currentRoute.value, router, useUserStore(pinia as any));

// varlet UI
// app.use(Varlet);
// ==================== 应用初始化辅助函数 ====================

/**
 * 初始化广告服务
 * 原生平台：同步等待初始化完成并隐藏启动页
 * Web平台：异步初始化，不阻塞应用启动
 */
const initAdService = async () => {
  if (platform.isMiniProgramEnv()) return;

  // 获取广告配置：1. 首次启动标识；2. 广告时长设置
  const isFirstLaunch = getsubKey('mobile', 'firstLaunch') !== 'true';
  const advertisingTime = getKeyJson('setting')?.advertising_time;

  // 首次启动(默认5s) 或 有时长配置才会执行初始化。否则非首次且无配置直接跳过。
  const needAd = isFirstLaunch || (advertisingTime !== undefined && advertisingTime !== null && advertisingTime !== '');
  if (Capacitor.isNativePlatform()) {
    try {
      if (needAd) await adService.init();
    } catch (e) {
      console.warn('广告初始化异常（原生）:', e);
    }
  } else {
    try {
      if (needAd) await adService.init();
    } catch (e) {
      console.warn('广告初始化异常（Web）:', e);
    }
  }
};

/**
 * 初始化状态栏适配器
 */
const initStatusBar = async () => {
  try {
    await statusBarAdapter.init({ overlaysWebView: true });
  } catch (e) {
    console.warn('状态栏适配器初始化失败:', e);
  }
};

/**
 * 初始化认证和路由
 */
const initAuthAndRoutes = async () => {
  try {
    await bootstrapAuthAndRoutes();
  } catch (e) {
    console.warn('认证和路由初始化失败:', e);
  }
};

/**
 * 权限检查：通过实际文件操作验证真实权限（跨平台）
 * @returns 是否有文件权限
 */
// const checkPermissions = async (): Promise<boolean> => {
//   try {
//     console.log('开始文件权限检查');
//     await createFile();
//     console.log('文件权限检查通过');
//     return true;
//   } catch (error) {
//     console.error('权限检查失败:', error);
//     return false;
//   }
// };

/**
 * 初始化需要文件权限的服务
 * 包括：日志服务、热更新服务
 */
const initFileBasedServices = async () => {
  try {
    await initLogger();
    await initHotUpdateManager();
    console.log('文件服务初始化完成');
  } catch (e) {
    console.warn('文件服务初始化失败:', e);
  }
};

/**
 * 初始化或恢复 mobile 配置
 * @returns mobile 配置对象
 */
const initMobileConfig = () => {
  const history = getKeyJson('mobile');
  const firstLaunch = history && typeof history === 'object' ? (history as any).firstLaunch : undefined;

  // 如果已有有效配置，直接返回
  if (
    history &&
    typeof history === 'object' &&
    'server_info' in history &&
    'tenant_id' in history &&
    Array.isArray(history.user_info)
  ) {
    return history;
  }
  // localStorage.clear();
  // 使用环境变量初始化默认配置
  const initialized = {
    server_info: {
      host: import.meta.env.VITE_BASE_API,
      iam: import.meta.env.VITE_IAM,
    },
    user_info: [],
    tenant_id: '',
    ...(firstLaunch !== undefined ? { firstLaunch } : {}),
  };

  setKeyJsonStr('mobile', initialized);
  return initialized;
};

// ==================== 应用初始化主流程 ====================

/**
 * 应用初始化主流程
 * 
 * 执行顺序：
 * 1. 基础服务初始化（状态栏、认证路由）
 * 2. 权限检查
 * 3. 文件服务初始化（仅在有权限时）
 * 4. Mobile配置初始化
 */
const initializeApplication = async () => {
  try {
    // 步骤1：基础服务初始化
    await initAuthAndRoutes();

    // 步骤2：权限检查
    // const hasPermission = await checkPermissions();

    // 步骤3：文件服务初始化（需要权限）
    // if (hasPermission) {
    //   await initFileBasedServices();
    //   console.log('应用初始化完成（包含文件服务）');
    // } else {
    //   console.log('应用初始化完成（无文件服务）');
    //   console.warn('由于权限未授予，Logger和热更新服务未启动');
    // }

    // 步骤4：Mobile配置初始化
    return initMobileConfig();
    
  } catch (error) {
    console.error('应用初始化失败:', error);
  }
};

/**
 * 应用启动引导函数
 * 
 * 执行顺序：
 * 1. 初始化广告服务
 * 2. 等待路由准备完成
 * 3. 挂载Vue应用
 * 4. 后台初始化其他服务
 */
const bootstrap = async () => {
  // 组件初始化
    // await loadComponent(); 
  // 步骤1：广告初始化
  await withTimeout(initAdService(), PRE_MOUNT_INIT_TIMEOUT_MS, 'initAdService');
  const hasStartupAd = adService.hasPending();
  adService.setStartupBlocking(hasStartupAd);

  // 原生平台：尽早初始化状态栏，避免首帧后再调整 overlays/statusbar 导致闪白或布局跳动
  if (Capacitor.isNativePlatform()) {
    await withTimeout(initStatusBar(), 800, 'initStatusBar');
  }
  // 步骤2：等待路由准备
  if (!hasStartupAd) {
    void router.isReady().catch((e) => console.warn('router ready failed:', e));
  }

  // 步骤3：挂载应用
  app.mount('#app');

  // 原生平台：确保首帧 UI 已渲染后再隐藏原生启动页，避免 splash 消失到页面可见之间出现白屏空窗
  await nextFrame();
  await hideNativeSplash(hasStartupAd ? 'ad-ready' : 'app-mounted');

  // if (Capacitor.isNativePlatform()) {
  //   try {
  //     await nextFrame();
  //     await SplashScreen.hide();
  //     console.log('Splash/Ad 耗时(ms)=', (performance.now() - (window as any).__t0) + 100);
  //   } catch (e) {
  //     console.warn('隐藏启动页失败:', e);
  //   }
  // }

  // 步骤4：后台初始化（不阻塞UI）
  initializeApplication().catch((e) => console.error('后台初始化失败:', e));
};

bootstrap();

// 暴露调试入口以便远程/手动触发热更新初始化调试
(window as any).initHotUpdateManager = initHotUpdateManager;

// DEV：在 Web 开发环境也预览更新确认弹窗（不依赖原生环境）
// if (import.meta.env.DEV && !Capacitor.isNativePlatform()) {
//   hotUpdateManager.previewConfirmModal({
//     version: '1.2.3',
//     description: 'Web 开发环境预览确认弹窗'
//   }).catch((e) => console.warn('Web 预览更新确认弹窗失败', e));
// }


